import { useEffect, useRef, useState } from 'react';
import styles from './modalPaymentReserve.module.scss';
import { IconButton, Radio, RadioGroup, FormControlLabel, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useToast } from '../../../../helpers/hooks';
import { useNavigate } from 'react-router-dom';
import Modal from '../../../Modal';
import { images } from '../../../../constants/images';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import { PaymentContent } from '../../../../pages/LinzeCheck/EntryPoint/components/ModalPayment/components/PaymentContent';
import { ModalPaymentReserveProps } from '../../../../interfaces/publication';
import ButtonShared from '../../../Button';

const ModalPaymentReserve = ({
    open,
    onClose,
    vehiclePrice,
    depositAmount = 200000,
    setOpenModalForm,
    reservar,
    getReservePublicationId,
    user,
    publication,
    getPaymentProcess,
    getDataPayment,
    dataPayment,
    getCancellationPayment,
    publicationId,
    brand,
    formData,
    setFormData,
    errors,
    setErrors,
    selectedVersion,
    dealershipSelected
}: ModalPaymentReserveProps) => {
    const pushToast = useToast();
    const navigate = useNavigate();
    const [paymentMethod, setPaymentMethod] = useState('online');
    const [isPolling, setIsPolling] = useState(false);
    const [isPaymentCancelled, setIsPaymentCancelled] = useState(false);
    const [wasPageReloaded, setWasPageReloaded] = useState(false);
    const [loading, setLoading] = useState(false);
    const intervalRef = useRef<NodeJS.Timeout>();
    const paymentWindowRef = useRef<Window | null>(null);

    const checkWindowStatus = () => {
        if (paymentWindowRef.current) {
            if (paymentWindowRef.current.closed) {
                setIsPaymentCancelled(true);
                stopPolling();
                handlePaymentProcess();
            }
        }
    };

    const startPolling = () => {
        setIsPolling(true);
        intervalRef.current = setInterval(() => {
            checkWindowStatus();
            handlePaymentProcess();
        }, 10000);
    };

    const stopPolling = () => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = undefined;
        }
        setIsPolling(false);
    };

    const handleClose = () => {
        if (loading) {

        } else {
            stopPolling();
            onClose();
            setOpenModalForm(false);
        }
    };

    const handleSubmit = () => {
        setLoading(true);
        const formattedAreaCode = user?.phone?.code.replace(/^\+/, '') || formData?.areaCode;

        if(publicationId){
            getReservePublicationId(
                publicationId, 
                {
                    name: user?.name || formData?.name,
                    last_name: user?.last_name || formData?.last_name,
                    legal_id_type: 'rut',
                    legal_id: user?.legal_id || formData?.rut,
                    email: user?.email || formData?.email,
                    phone: formattedAreaCode + user?.phone?.number || formData?.phone,
                },
                brand ? 'brand' : publication?.type_publication || 'used',
                selectedVersion || publication?.vehicle_data?.version || '',
                publication?.pictures?.gallery?.[0]?.path || publication?.pictures?.[0]?.path || '',
                publication?.price || vehiclePrice || 0,
                dealershipSelected || ''
            );
        } else {
            getReservePublicationId(
                publication?._id, 
                {
                    name: user?.name || formData?.name,
                    last_name: user?.last_name || formData?.last_name,
                    legal_id_type: 'rut',
                    legal_id: user?.legal_id || formData?.rut,
                    email: user?.email || formData?.email,
                    phone: formattedAreaCode + user?.phone?.number || formData?.phone,
                },
                brand ? 'brand' : publication?.type_publication || 'used',
                selectedVersion || publication?._id || '',
                publication?.pictures?.gallery?.[0]?.path || publication?.pictures?.[0]?.path || '',
                publication?.price || vehiclePrice || 0,
                dealershipSelected || ''
            );
        }
    };

    const handlePaymentProcess = () => {
        if (!dataPayment?.data?.payment_process?._id) return;
        getPaymentProcess(dataPayment?.data?.payment_process?._id);
    }

    const formatPrice = (price: number) => {
        return `$${price?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".")}`;
    };

    const handleRedirectPayment = () => {
        const paymentUrl = dataPayment?.data?.payment_process?.external_payment_data?.url || sessionStorage.getItem('paymentUrlAfterReload');
        
        if (!paymentUrl) {
            console.log('No payment URL available');
            return;
        }
        
        handlePaymentProcess();
        paymentWindowRef.current = window.open(paymentUrl, '_blank');
        if (paymentWindowRef.current) {
            paymentWindowRef.current.focus();
            startPolling();
            } else {
                console.log('Failed to open payment window - popup might be blocked');
            }
    };

    const handleCloseClick = () => {
        stopPolling();
        onClose();
        window.location.reload();
    };

    const handleCloseClickProcess = () => {
        stopPolling();
        if(dataPayment?.data?.payment_process?.status !== 'approved'){
            console.log('Payment cancelled');
            setIsPaymentCancelled(true);
        }
        paymentWindowRef.current?.close();
    };

    const getPaymentStatusContent = () => {
        switch (dataPayment?.data?.payment_process?.status) {
            case 'pending':
            case 'processing':
                return (
                    <>
                        <div className={styles.closeButton}>
                            <IconButton onClick={handleCloseClickProcess}>
                                <CloseIcon />
                            </IconButton>
                        </div>
                        <CircularProgress color="inherit" className={styles.processingIcon} />
                        <p className={styles.statusText}>En proceso...</p>
                    </>
                );
            case 'approved':
                return (
                    <>
                        <div className={styles.closeButton}>
                            <IconButton onClick={handleCloseClick}>
                                <CloseIcon />
                            </IconButton>
                        </div>
                        <TaskAltOutlinedIcon className={styles.aceptIcon} />
                        <p className={`${styles.statusText} ${styles.approved}`}>Pago aprobado</p>
                    </>
                );
            case 'cancelled':
            case 'rejected':
                return (
                    <>
                        <div className={styles.closeButton}>
                            <IconButton onClick={handleCloseClick}>
                                <CloseIcon />
                            </IconButton>
                        </div>
                        <CancelOutlinedIcon className={styles.cancelIcon} />
                        <p className={`${styles.statusText} ${styles.cancelled}`}>Pago cancelado</p>
                    </>
                );
            default:
                return (
                    <>
                        <div className={styles.closeButton}>
                            <IconButton onClick={handleCloseClickProcess}>
                                <CloseIcon />
                            </IconButton>
                        </div>
                        <CircularProgress color="inherit" className={styles.processingIcon} />
                        <p className={styles.statusText}>En proceso...</p>
                    </>
                );
        }
    };

    useEffect(() => {
        if (reservar?.data?.data?.process_info?._id) {
            const newPaymentData = {
                paymentMethod: "paga-en-linea",
                purchase_summary: {
                    total: 200000,
                },
                origin: "web",
                reason: "reserve",
                process_info: reservar?.data?.data?.process_info?._id,
                user: {
                    name: user?.name || formData?.name,
                    last_name: user?.last_name || formData?.last_name,
                    legal_id: user?.legal_id || formData?.rut,
                    email: user?.email || formData?.email,
                },
                plate: reservar?.data?.data?.process_info?.product?.plate,
            };
            getDataPayment(newPaymentData);
        }
    }, [reservar?.data?.data?.process_info]);

    useEffect(() => {
        if (dataPayment?.data?.payment_process?.external_payment_data?.url) {
            handlePaymentProcess();
            paymentWindowRef.current = window.open(dataPayment?.data?.payment_process?.external_payment_data?.url, '_blank');
            if (paymentWindowRef.current) {
                paymentWindowRef.current.focus();
                startPolling();
            } else {
                console.log('Failed to open payment window - popup might be blocked');
            }
        }
    }, [dataPayment?.data?.payment_process?.external_payment_data]);
    
    useEffect(() => {
        return () => {
            stopPolling();
            if (paymentWindowRef.current && !paymentWindowRef.current.closed) {
                console.log('Closing payment window on unmount');
                paymentWindowRef.current.close();
            }
        };
    }, []);

    const cancellationCalled = useRef(false);

    useEffect(() => {
        if (!cancellationCalled.current &&
            dataPayment?.data?.payment_process?._id &&
            (dataPayment?.data?.payment_process?.status === 'rejected'
                || dataPayment?.data?.payment_process?.status === 'cancelled'
                || isPaymentCancelled)) {
            cancellationCalled.current = true;
            getCancellationPayment(dataPayment?.data?.payment_process?._id);
        }
    }, [dataPayment?.data?.payment_process?.status, dataPayment?.data?.payment_process?._id, isPaymentCancelled]);


    useEffect(() => {
        if(reservar?.fetching === 'failure'){
            onClose();
            setLoading(false);
            pushToast('Error al reservar el vehículo');
        }
    }, [reservar]);
        

    return (
        <Modal variant="" open={open} className={styles.modal}>
            {dataPayment?.data?.payment_process ? (
                <PaymentContent
                    handleCloseClick={handleCloseClick}
                    isPaymentCancelled={isPaymentCancelled}
                    getPaymentStatusContent={getPaymentStatusContent}
                />
            ) : (
                <div className={styles.content}>
                    <div className={styles.closeButton}>
                        <CloseIcon onClick={() => handleClose()} />
                    </div>

                    <h1 className={styles.title}>
                        Reserva tu vehículo al instante
                    </h1>
                    <p className={styles.description}>
                        Reserva tu auto en un click pagando de contado. Vas a ser redirigido al medio
                        de pago y luego volverás automáticamente a Linze
                    </p>

                    <div className={styles.priceContainer}>
                        <div className={styles.priceBox}>
                            <h2 className={styles.price}>{formatPrice(vehiclePrice)}</h2>
                            <p className={styles.priceLabel}>Valor del vehículo</p>
                        </div>
                        <div className={styles.priceDivider}></div>
                        <div className={styles.priceBox}>
                            <h2 className={styles.price}>{formatPrice(depositAmount)}</h2>
                            <p className={styles.priceLabel}>Valor de la reserva</p>
                        </div>
                    </div>
                    <div className={styles.paymentSection}>
                        <h2 className={styles.paymentTitle}>Selecciona una forma de pago</h2>
                        <div className={styles.paymentOption}>
                            <input type="radio" id="pagaEnLinea" name="payment" value="Paga en linea" checked readOnly />
                            <label htmlFor="pagaEnLinea" className={styles.paymentLogo}>
                                <img src={images.pagaEnLinea} alt="PagaEnLinea" title="PagaEnLinea" className={styles.pagalineaImage} />
                            </label>
                        </div>
                    </div>
                    <div className={styles.actions}>
                        <button className={styles.cancelButton} onClick={() => handleClose()}>
                            Cancelar
                        </button>
                        <button
                            className={loading ? styles.enviarButtonDisabled : styles.enviarButton}
                            disabled={loading ? true : false}
                            onClick={() => handleSubmit()}
                        >
                            {loading ? <CircularProgress size={24} style={{ color: 'white' }} /> : 'Continuar'}
                        </button>
                    </div>
                </div>
            )}

        </Modal>
    );
};

export default ModalPaymentReserve;
